import { PreviewMediaType } from '@/types';
import { optimizeImageUrl } from '@/utils/media/optimizeImageUrl';

import type { LibraryItem, Media } from '@/types';

export type PresetPreview = {
  /** The still. Also the poster while this slide is off screen. */
  url: string;
  /** The clip, for a video preset. Absent for images. */
  videoUrl?: string;
};

export type PresetPreviews = {
  items: PresetPreview[];
};

type GetPresetPreviewsParams = {
  item: Pick<LibraryItem, 'type' | 'mediaList'> & { media?: Media };
  /**
   * Longest side asked of the CDN, in px. Omitted by the tiles, which take
   * `optimizeImageUrl`'s thumbnail default; passed by the full-bleed surfaces,
   * where a thumbnail would be upscaled to fill the screen.
   */
  size?: number;
};

/**
 * Every preview a multi-output preset holds, or `undefined` to keep the single
 * one it has always had.
 *
 * Every surface that shows a preset asks the same question, so it is answered
 * once: a tile that decided differently from its neighbour would make the same
 * preset look like two different things depending on where it was found. Only
 * the *resolution* is the caller's to choose — a 400px still is right in a grid
 * and soft across a full-screen slide — and that changes how a preview is
 * fetched, never which previews there are.
 *
 * Video presets carry both the poster and the clip. The carousel plays only the
 * slide on screen, so a tile costs one decoder however many outputs there are —
 * which is what makes playing tiles affordable, and why they no longer have to
 * settle for stills.
 */
const getPresetPreviews = ({
  item,
  size,
}: GetPresetPreviewsParams): PresetPreviews | undefined => {
  if (!item.mediaList || item.mediaList.length < 2) return undefined;

  const isVideo = item.type === PreviewMediaType.VIDEO;

  // A video component's `url` is the MP4; its still lives in `poster`. Handing
  // an `<img>` the MP4 is the trap this ordering avoids. Only the still is
  // resized — the clip is served as published.
  const items = item.mediaList
    .map((media) => ({
      url: optimizeImageUrl(isVideo ? media.poster : media.url, { size }),
      ...(isVideo && media.url ? { videoUrl: media.url } : {}),
    }))
    .filter((preview): preview is PresetPreview => Boolean(preview.url));

  if (items.length < 2) return undefined;

  return { items };
};

export default getPresetPreviews;
